import { createSlice, createAsyncThunk, AnyAction } from '@reduxjs/toolkit';
import { PermissionDataArray, PermissionModel, PermissionQueryParams, PermissionItem } from '@app/interfaces/permission';
import { createPermissionApi, deletePermissionApi, updatePermissionApi, getPermissionsApi } from '@app/api/permission.api';
import { message } from 'antd';
import { showError } from '@app/utils/message';

export interface PermissionState {
  data: PermissionModel[];
  totalRows: number;
  pagination: {
    current: number,
    pageSize: number
  },
  filters: any,
  sorter: any,
  searchTerm: string;
  selectedRecord: PermissionItem[];
  loading: boolean;
  error: string | null;
}

const initialState: PermissionState = {
  data: [],
  totalRows: 0,
  pagination: { current: 1, pageSize: 1000 },
  filters: {},
  sorter: {},
  searchTerm: "",
  selectedRecord: [],
  loading: false,
  error: null
};

interface ResponseData {
  data: PermissionItem;
  message: string;
}

export const getPermissions = createAsyncThunk<PermissionDataArray, PermissionQueryParams>(
  'permission/getPermissions',
  async (params: PermissionQueryParams = {}, { rejectWithValue }) => {
    try {
      const data = (await getPermissionsApi(params)) as PermissionDataArray;
      return data || [];
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'An error occurred');
    }
  },
);

export const createPermission = createAsyncThunk<ResponseData, PermissionModel>(
  'permission/createPermission', async (data) => {
  const response = await createPermissionApi(data);
  return response;
});

export const updatePermission = createAsyncThunk<ResponseData, { userId: number; formData: PermissionModel }>(
  'permission/updatePermission',
  async ({ userId, formData }) => {
    const response = await updatePermissionApi(userId, formData);
    return response;
  },
);

export const deletePermission = createAsyncThunk<{ userId: number; response: any }, number>(
  'permission/deletePermission',
  async (userId, { rejectWithValue }) => {
    try {
      const response = await deletePermissionApi(userId);
      return { userId, response };
    } catch (error) {
      // Handle errors with rejectWithValue
      return rejectWithValue(error instanceof Error ? error.message : 'An error occurred');
    }
  },
);

const isRejectedAction = (action: AnyAction) => {
  return action.type.endsWith('rejected');
};

export const permissionSlice = createSlice({
  name: 'permission',
  initialState,
  reducers: {
    updateTableParams(state, action) {
      const { pagination, filters, sorter, searchTerm } = action.payload;
      state.pagination = pagination;
      state.filters = filters;
      state.sorter = sorter;
      state.searchTerm = searchTerm;
    }
  },
  extraReducers: (builder) => {
    builder
    .addCase(getPermissions.pending, (state) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(getPermissions.fulfilled, (state, action) => {
      const { rows, total_rows } = action.payload;

      const data = rows;
      state.data = data.map((item: PermissionItem) => item.attributes) || [];

      state.totalRows = parseInt(total_rows, 10);
      state.loading = false;
    })
    .addCase(createPermission.fulfilled, (state, action) => {
      const { data, message: newMessage } = action.payload;
      state.data.unshift(data.attributes);
      message.success(newMessage);
    })
    .addCase(updatePermission.fulfilled, (state, action) => {
      const { data, message: newMessage } = action.payload;
      const userId = typeof data.id === 'string' ? parseInt(data.id, 10) : data.id;

      const updatedIndex = state.data.findIndex((item) => item.id === userId);
      if (updatedIndex !== -1) {
        state.data[updatedIndex] = data.attributes;
        message.success(newMessage);
      }
    })
    .addCase(deletePermission.fulfilled, (state, action) => {
      const { userId, response } = action.payload;

      if (response.error) { 
        showError(response.error); return;
      }
      else {
        state.data = state.data.filter((order) => order.id !== userId);
        message.success(response.message);
      }
    })
    .addMatcher(isRejectedAction, (state, action) => {
      state.loading = false;
      // rejectWithValue trong slice này truyền chuỗi (vd lý do không thể xoá quyền).
      const payloadMessage = typeof action.payload === 'string' ? action.payload : undefined;
      showError(payloadMessage || action.payload?.message || action.payload?.error || action?.error?.message);
    });
  },
});

export const { updateTableParams } = permissionSlice.actions;

export default permissionSlice.reducer;
